import { useUpdateSmartList } from 'features/smart-lists';
import { useUpdateTasks } from 'features/tasks';
import { useCallback } from 'react';
import { useUpdateRelationshipsReferences } from 'shared/hooks/use-update-relationships-references';
import {
  DocumentOrganizationKeys,
  FilterDocument,
} from 'shared/types/filter-document';
import { ID } from 'shared/types/id';
import { RelationshipUpdate } from 'shared/types/relationship';
import { SmartList } from 'shared/types/smart-list';
import { GroupByMode, SortingMode } from 'shared/types/sorting-mode';
import { Task } from 'shared/types/task';

export const useSmartListOnReorder = (
  filterDocument: FilterDocument,
  smartList?: SmartList,
) => {
  const { submit: updateSmartList } = useUpdateSmartList();
  const { submit: updateTasks } = useUpdateTasks();
  const updateRelationships = useUpdateRelationshipsReferences();

  return useCallback(
    (
      updatedSections: { id: ID; taskSorting: ID[] }[],
      updatedTasks: Task[],
      relationshipUpdates?: RelationshipUpdate[],
    ) => {
      if (!smartList) {
        return;
      }

      // Handle task updates (e.g. reparenting/move)
      updateTasks(updatedTasks);
      const groupBy =
        filterDocument[DocumentOrganizationKeys.GroupByMode] ??
        GroupByMode.Priority;

      // Handling "No Grouping"
      if (groupBy === GroupByMode.None) {
        const newSorting = updatedSections[0]?.taskSorting?.filter(
          (id) => id !== 'new-task',
        );

        if (newSorting) {
          updateSmartList({
            id: smartList.id,
            taskSorting: newSorting,
            filterDocument: {
              ...smartList.filterDocument,
              sortingMode: SortingMode.Custom,
            },
          });
        }

        if (relationshipUpdates?.length) {
          updateRelationships(relationshipUpdates);
        }
        return;
      }

      // handle sorting update for all groupings except 'noGrouping'
      const groupBySorting = updatedSections.reduce<Record<ID, ID[]>>(
        (acc, section) => {
          acc[section.id] = section.taskSorting;
          return acc;
        },
        {},
      );

      if (Object.keys(groupBySorting).length) {
        updateSmartList({
          id: smartList.id,
          customTaskOrder: {
            ...(smartList.customTaskOrder ?? {}),
            [groupBy]: groupBySorting,
          },
          filterDocument: {
            ...smartList.filterDocument,
            sortingMode: SortingMode.Custom,
          },
        });
      }

      if (relationshipUpdates?.length) {
        updateRelationships(relationshipUpdates);
      }
    },
    [
      filterDocument,
      smartList,
      updateSmartList,
      updateRelationships,
      updateTasks,
    ],
  );
};
